'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { fytofarmLogoDimensions } from '@eliadmin/core/src/utils/fytofarm-logo-dimensions';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useState, useRef } from 'react';

import { useRouter } from 'next/navigation';

import type {
  CategoriesEntity,
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';

import { SearchButton } from './search-button';
import { HeaderProfileButton } from './header-profile-button';
import { HeaderFavoritesButton } from './header-favorites-button';
import { HeaderCartButton } from './header-cart-button';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';

import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { LuPlus } from 'react-icons/lu';

export function HeaderContent({
  scrolled,
  generalLayout,
  menu,
  categories,
  searchBarScrolled,
  setIsSearchOpen,
  setIsSidebarOpen
}: {
  scrolled: boolean;
  generalLayout: GeneralLayout['data'];
  menu: Menu;
  categories: CategoriesEntity[];
  searchBarScrolled: boolean;
  setIsSearchOpen: (open: boolean) => void;
  setIsSidebarOpen: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();
  const logoDimensions = fytofarmLogoDimensions(generalLayout?.header?.logo);

  const openMenu = () => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setOpen(true);
  };

  const closeMenu = () => {
    closeTimeout.current = setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  const handleCategoryClick = (href: string) => {
    setOpen(false);
    router.push(href);
  };

  const plusMenuIndex = 2;

  return (
    <>
      <div
        className={cn(
          'custom-section',
          'ea-header-main__wrapper relative z-[39] bg-white py-4 sm:py-6 lg:py-8 xl:py-[34px]',
          scrolled && 'ea-header-main__scrolled',
        )}
      >
        <div
          className={cn(
            'custom-container',
            'relative hidden items-center justify-between lg:flex',
            'ea-header-content__desktop',
          )}
        >
          <Link prefetch={false} href={ROUTES.HOME()}>
            <Image
              src={generalLayout?.header?.logo?.url ?? ''}
              alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company ?? 'Fytofarm')}
              width={logoDimensions?.width ?? 0}
              height={logoDimensions?.height ?? 0}
              className="h-8 w-auto object-contain"
            />
          </Link>

          <nav className="relative z-[39] flex items-center justify-center gap-6">
            {menu.fields.map((item, index) => {
              const isPlus = index === plusMenuIndex;

              return (
                <div
                  key={item.name}
                  onClick={() => setOpen(false)}
                  onMouseEnter={isPlus ? openMenu : undefined}
                  onMouseLeave={isPlus ? closeMenu : undefined}
                >
                  <Link
                    href={getSafeHref(item?.path ?? '#')}
                    className="flex items-center gap-1 text-base text-black transition hover:opacity-80"
                  >
                    {item.name}
                    {isPlus && (
                      <LuPlus
                        className={cn(
                          'h-4 w-4 text-primary transition-transform',
                          open && 'rotate-45',
                        )}
                      />
                    )}
                  </Link>
                </div>
              );
            })}
          </nav>
          <div className="flex items-center justify-end gap-6">
            <SearchButton scrolled={searchBarScrolled} setIsSearchOpen={setIsSearchOpen} />
            <HeaderProfileButton />
            <HeaderFavoritesButton />
            <HeaderCartButton />
            <div className="h-2 w-2 rounded-full bg-[#F3F4F6]" />
          </div>
        </div>
        <div
          className={cn(
            'flex w-full items-center justify-between lg:hidden',
            'ea-header-content__mobile',
          )}
        >
          <div className="flex items-center justify-start gap-6 sm:gap-8">
            <MobileSidebar
              categories={categories}
              menu={menu.fields}
              side="left"
              setIsSidebarOpen={setIsSidebarOpen}
            />
            <Link prefetch={false} href={ROUTES.HOME()}>
              <Image
                src={generalLayout?.header?.logo?.url ?? ''}
                alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company ?? 'Fytofarm')}
                width={logoDimensions?.width ?? 0}
                height={logoDimensions?.height ?? 0}
                className="h-5 w-auto object-contain sm:h-6 md:h-7"
              />
            </Link>
          </div>
          <SearchButton scrolled={searchBarScrolled} setIsSearchOpen={setIsSearchOpen} />
        </div>
      </div>

      <div
        onMouseEnter={openMenu}
        onMouseLeave={closeMenu}
        className={cn(
          'custom-section absolute left-0 top-full z-[40] w-full bg-white shadow-xl transition-all duration-200',
          open
            ? 'pointer-events-auto translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-2 opacity-0',
        )}
      >
        <div className="custom-container grid grid-cols-5 gap-6 py-8">
          {categories.map(category => (
            <CategoryCard key={category.slug} category={category} onClick={handleCategoryClick} />
          ))}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[30] animate-fadeIn bg-black/30" onMouseEnter={closeMenu} />
      )}
    </>
  );
}

function CategoryCard({
  category,
  onClick,
}: {
  category: CategoriesEntity;
  onClick: (href: string) => void;
}) {
  const href = ROUTES.CATEGORY({ slug: category.slug, page: 1 });

  return (
    <button
      onClick={() => onClick(href)}
      className="col-span-1 flex flex-col gap-6 rounded-[16px] bg-[#F9FAFB] p-6 text-left transition-all hover:opacity-70"
    >
      <div className="flex w-full items-start justify-between">
        <Image
          src={category?.icon?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(category?.icon, category?.name)}
          width={48}
          height={48}
          className="object-contain"
        />
        <MdOutlineKeyboardArrowRight size={24} className="text-primary" />
      </div>
      <span data-ea-visual-tag="h5" className="block text-lg font-medium tracking-tighter lg:text-xl">{category.name}</span>
    </button>
  );
}
